import { useCallback, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../lib/api'
import { Button } from '../components/ui/Button'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Breadcrumb } from '../components/ui/Breadcrumb'
import { EmptyState } from '../components/ui/EmptyState'
import { TableSkeleton } from '../components/ui/Skeletons'
import { useToast } from '../components/ui/Toast'
import { useBrandingStore } from '../store/branding'
import type { Invoice } from '@shared/types'
import '../styles/invoice-document.css'

interface InvoiceLineItem {
  id: string
  description: string
  quantity: number
  unit_price: number
  total: number
  sort_order: number
}

interface InvoiceClient {
  company_name: string | null
  contact_name: string | null
  email: string | null
  phone: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state: string | null
  zip: string | null
}

interface InvoiceDocument extends Invoice {
  line_items: InvoiceLineItem[]
  client: InvoiceClient | null
  project: { name: string | null } | null
}

const PrinterIcon = (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
)

function fmtMoney(n: number | null | undefined): string {
  return '$' + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—'
  // SQLite hands back "YYYY-MM-DD HH:MM:SS"; Safari will not parse the space form.
  const parsed = new Date(d.replace(' ', 'T'))
  if (isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

export default function InvoiceDetail() {
  const { slug } = useParams<{ slug: string }>()
  const { toast, toastError } = useToast()
  const branding = useBrandingStore()
  const [invoice, setInvoice] = useState<InvoiceDocument | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setInvoice(await api.get<InvoiceDocument>(`/invoices/${slug}`))
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load invoice')
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => { load() }, [load])

  function downloadPdf() {
    toast('Opening your print dialog — choose "Save as PDF"', 'info')
    // Let the toast paint before the dialog blocks the main thread.
    requestAnimationFrame(() => {
      try { window.print() } catch (err) { toastError('Could not open the print dialog', err) }
    })
  }

  if (loading) return <div className="page-enter"><TableSkeleton /></div>

  if (error || !invoice) {
    return (
      <div className="page-enter">
        <EmptyState
          title="That invoice could not be loaded"
          description={error || 'The invoice may have been deleted.'}
          action={<Link to="/invoices"><Button>Back to Invoices</Button></Link>}
        />
      </div>
    )
  }

  const client = invoice.client
  const items = [...(invoice.line_items || [])].sort((a, b) => a.sort_order - b.sort_order)
  const cityLine = [client?.city, client?.state].filter(Boolean).join(', ')
  const cityStateZip = [cityLine, client?.zip].filter(Boolean).join(' ')

  return (
    <div className="page-enter">
      {/* Screen-only chrome — hidden on paper by invoice-document.css */}
      <div className="inv-screen-only">
        <Breadcrumb items={[{ label: 'Invoices', to: '/invoices' }, { label: invoice.invoice_number ?? invoice.slug }]} />
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
              {invoice.invoice_number ?? invoice.slug}
            </h1>
            <StatusBadge status={invoice.status} />
          </div>
          <Button icon={PrinterIcon} onClick={downloadPdf}>Download PDF</Button>
        </div>
      </div>

      {/* The document itself — this is what prints */}
      <div className="inv-document">
        <div className="inv-doc-header">
          <div className="inv-doc-issuer">
            {branding.logoFull || branding.logoIcon ? (
              <img
                className="inv-doc-logo"
                src={branding.logoFull || branding.logoIcon || ''}
                alt={branding.companyName}
              />
            ) : (
              <div className="inv-doc-issuer-name">{branding.companyName}</div>
            )}
          </div>
          <div className="inv-doc-title-block">
            <div className="inv-doc-title">INVOICE</div>
            <div className="inv-doc-number">{invoice.invoice_number ?? invoice.slug}</div>
          </div>
        </div>

        <div className="inv-doc-meta">
          <div className="inv-doc-billto">
            <div className="inv-doc-label">Bill To</div>
            <div className="inv-doc-billto-name">{client?.company_name || client?.contact_name || '—'}</div>
            {client?.company_name && client?.contact_name && (
              <div className="inv-doc-billto-line">{client.contact_name}</div>
            )}
            {client?.address_line1 && <div className="inv-doc-billto-line">{client.address_line1}</div>}
            {client?.address_line2 && <div className="inv-doc-billto-line">{client.address_line2}</div>}
            {cityStateZip && <div className="inv-doc-billto-line">{cityStateZip}</div>}
            {client?.email && <div className="inv-doc-billto-line">{client.email}</div>}
            {client?.phone && <div className="inv-doc-billto-line">{client.phone}</div>}
          </div>

          <div className="inv-doc-facts">
            <div className="inv-doc-fact">
              <span className="inv-doc-label">Issued</span>
              <span className="inv-doc-fact-value">{fmtDate(invoice.created_at)}</span>
            </div>
            <div className="inv-doc-fact">
              <span className="inv-doc-label">Due</span>
              <span className="inv-doc-fact-value">{fmtDate(invoice.due_date)}</span>
            </div>
            {invoice.project?.name && (
              <div className="inv-doc-fact">
                <span className="inv-doc-label">Project</span>
                <span className="inv-doc-fact-value">{invoice.project.name}</span>
              </div>
            )}
            <div className="inv-doc-fact">
              <span className="inv-doc-label">Status</span>
              <span className="inv-doc-fact-value inv-doc-status">{invoice.status}</span>
            </div>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="inv-doc-noitems">
            This invoice has no line items yet.
          </div>
        ) : (
          <table className="inv-doc-table">
            <thead>
              <tr>
                <th className="inv-doc-th-desc">Description</th>
                <th className="inv-doc-th-num">Qty</th>
                <th className="inv-doc-th-num">Unit Price</th>
                <th className="inv-doc-th-num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((li) => (
                <tr key={li.id}>
                  <td className="inv-doc-td-desc">{li.description}</td>
                  <td className="inv-doc-td-num">{li.quantity}</td>
                  <td className="inv-doc-td-num">{fmtMoney(li.unit_price)}</td>
                  <td className="inv-doc-td-num">{fmtMoney(li.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="inv-doc-totals">
          <div className="inv-doc-total-row">
            <span>Subtotal</span><span>{fmtMoney(invoice.subtotal)}</span>
          </div>
          {!!invoice.discount_amount && (
            <div className="inv-doc-total-row">
              <span>Discount</span><span>−{fmtMoney(invoice.discount_amount)}</span>
            </div>
          )}
          {!!invoice.tax_amount && (
            <div className="inv-doc-total-row">
              <span>Tax{invoice.tax_rate ? ` (${invoice.tax_rate}%)` : ''}</span>
              <span>{fmtMoney(invoice.tax_amount)}</span>
            </div>
          )}
          <div className="inv-doc-total-row inv-doc-total-grand">
            <span>Total</span><span>{fmtMoney(invoice.total)}</span>
          </div>
          {!!invoice.amount_paid && (
            <div className="inv-doc-total-row">
              <span>Paid</span><span>−{fmtMoney(invoice.amount_paid)}</span>
            </div>
          )}
          <div className="inv-doc-total-row inv-doc-total-due">
            <span>Balance Due</span><span>{fmtMoney(invoice.balance_due)}</span>
          </div>
        </div>

        {invoice.notes && (
          <div className="inv-doc-notes">
            <div className="inv-doc-label">Notes</div>
            <div className="inv-doc-notes-body">{invoice.notes}</div>
          </div>
        )}

        <div className="inv-doc-footer">
          {branding.footerText || `Thank you for your business — ${branding.companyName}`}
        </div>
      </div>
    </div>
  )
}
